import React from 'react';
import { Button, Grid, Link, List, ListItem, ListItemText, Tooltip, Typography } from '@material-ui/core/';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { lowesDomainUrl, productJsonProps } from '../../Constants/Products';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import classNames from 'classnames';
import { get } from 'lodash';
import ProductBuyable from './ProductBuyable';

//leaving both of these redundant styles to come back to if i need to do different styling based on list/grid in the future
const useStyles = makeStyles((theme) => ({
	list: {
		padding: '1rem',
	},
	listGreyColor: {
		backgroundColor: theme.palette.neutral.n100,
	},
	grid: {
		backgroundColor: theme.palette.neutral.n100,
		padding: '1rem',
	},
}));

const pdpUrlName = 'Pdp Url';
const copyIconTooltip = 'Click to copy to the clipboard.';
const includeCopyIcon = [
	'Barcode',
	'Item Number',
	'Vendor Number',
	'Omni ID/Product ID',
	'Gtin ID',
	'Model ID',
];

const productPropMapLeftCol = [
	{
		name: 'Name',
		json: productJsonProps.name,
	},
	{
		name: 'Brand',
		json: productJsonProps.brand,
	},
	{
		name: 'Category',
		json: productJsonProps.category,
	},
	{
		name: 'Division',
		json: productJsonProps.division,
	},
	{
		name: pdpUrlName,
		json: productJsonProps.url,
	},
];
const productPropMapMidCol = [
	{
		name: 'Buyable',
		json: productJsonProps.buyable,
	},
	{
		name: 'Published',
		json: productJsonProps.published,
	},
	{
		name: 'Status',
		json: productJsonProps.status,
	},
	{
		name: 'Start Date',
		json: productJsonProps.startDate,
	},
	{
		name: 'Barcode',
		json: productJsonProps.barcode,
	},
];
const productPropMapRightCol = [
	{
		name: 'Omni ID/Product ID',
		json: productJsonProps.productId,
	},
	{
		name: 'Gtin ID',
		json: productJsonProps.gtinId,
	},
	{
		name: 'Item Number',
		json: productJsonProps.itemNumber,
	},
	{
		name: 'Vendor Number',
		json: productJsonProps.vendorNumber,
	},
	{
		name: 'Model ID',
		json: productJsonProps.modelId,
	},
];

const ProductValueWithCopyIcon = ({value}) => {
	return (
		<Tooltip title={copyIconTooltip} arrow={true} placement="top">
			<Button
				style={{ fontSize: '1rem', cursor: 'copy' }}
				color="inherit"
				startIcon={<CopyIcon />}
				onClick={() => {
					navigator.clipboard.writeText(value);
				}}
			>
				{value}
			</Button>
		</Tooltip>
	);
}

const RenderPropVal = ({ product, sectionObj }) => {
	// prepend lowes.com base url before pd url.
	if (sectionObj.name === pdpUrlName) {
		if (product?.product?.pdURL) {
			product.product.pdURL = `${lowesDomainUrl}${product.product.pdURL}`;
		}
	}
	// add copy icon for numeric values
	if (includeCopyIcon.includes(sectionObj.name)) {
		if (product) {
			const value = get(product, sectionObj.json, '');
			return <ProductValueWithCopyIcon value={value} />
		} else {
			return '';
		}
	}
	// buyable or published
	else if (sectionObj.name === 'Buyable' || sectionObj.name === 'Published') {
		const result = get(product, sectionObj.json, '');
		return <ProductBuyable isBuyable={result} />;
	}
	// If pdp generate URL
	else if (sectionObj.name === pdpUrlName) {
		const url = get(product, sectionObj.json, '');
		return (
			<Link href={url} target="_blank">
				{url}
			</Link>
		);
	}
	// get categories
	else if (sectionObj.name === 'Category') {
		const result = Object.values(get(product, sectionObj.json, ''));
		return result.reduce((acc, cur) => {return acc + cur + ', '}, '').slice(0,-2);
	}
	// format date
	else if (sectionObj.name === 'Start Date') {
		const result = new Date(get(product, sectionObj.json, '')).toDateString();
		return result;
	}
	else {
		return get(product, sectionObj.json, 'Not Found');
	}
};
// Render list section
const renderSectionItems = (product) => {
	return product.sectionObj.map((p) => {
		return (
			<ListItem disableGutters key={p.name}>
				<ListItemText
					primary={<Typography variant="subtitle1">{p.name + ':'}</Typography>}
					secondary={<RenderPropVal product={product} sectionObj={p} />}
				/>
			</ListItem>
		);
	});
};

const ProductDetails = ({ product, isListLayout, isFromManageProduct }) => {
	const classes = useStyles();
	const RenderProductInfoList = (product) => {
		return (
			<Grid container item direction="column">
				<Grid item>
					<List dense disablePadding>
						{renderSectionItems(product)}
					</List>
				</Grid>
			</Grid>
		);
	};
	const RenderListLayout = (product) => {
		const listViewClasses = classNames({
			[classes.list]: true,
			// only add grey color background on list view, when not displaying on manage product page.
			[classes.listGreyColor]: !isFromManageProduct,
		});
		return (
			<Grid className={listViewClasses} container item data-cy="product-details-container">
				<Grid item xs={4}>
					<RenderProductInfoList {...product} sectionObj={productPropMapLeftCol} />
				</Grid>
				<Grid item xs={4}>
					<RenderProductInfoList {...product} sectionObj={productPropMapMidCol} />
				</Grid>
				<Grid item xs={4}>
					<RenderProductInfoList {...product} sectionObj={productPropMapRightCol} />
				</Grid>
			</Grid>
		);
	};
	const RenderGridLayout = (product) => {
		return (
			<Grid className={classes.grid} container item data-cy="product-details-container" direction="column">
				<Grid item>
					<RenderProductInfoList {...product} sectionObj={productPropMapLeftCol} />
				</Grid>
				<Grid item>
					<RenderProductInfoList {...product} sectionObj={productPropMapMidCol} />
				</Grid>
				<Grid item>
					<RenderProductInfoList {...product} sectionObj={productPropMapRightCol} />
				</Grid>
			</Grid>
		);
	};
	return isListLayout ? <RenderListLayout {...product} /> : <RenderGridLayout {...product} />;
};

export default ProductDetails;
